import { type FormEvent, useState, useEffect } from 'react';
import { AlertCircle, Download, FileText, Layers, Loader2, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CubixApiError } from '@/lib/api/types';
import { cn, formatAmount } from '@/lib/utils';

import { useReportConfigs } from '@/features/parameters/reportConfigApi';
import type { ReportConfig, GapMatrixParams } from '@/features/parameters/reportConfigApi';

import { useGapMatrix, downloadGapExport } from './api';
import type { GapMatrixCell, GapMatrixRow, MatrixDimension } from './types';

// ── Colour helpers ─────────────────────────────────────────────────────────────

function netClass(net: string): string {
  const v = Number(net);
  if (v > 0)  return 'text-emerald-700 dark:text-emerald-400';
  if (v < 0)  return 'text-destructive';
  return 'text-muted-foreground';
}

function cellBg(net: string): string {
  const v = Number(net);
  const abs = Math.abs(v);
  if (v > 0) {
    if (abs > 500_000_000) return 'bg-emerald-500/20';
    if (abs > 100_000_000) return 'bg-emerald-500/10';
    return 'bg-emerald-500/5';
  }
  if (v < 0) {
    if (abs > 500_000_000) return 'bg-destructive/20';
    if (abs > 100_000_000) return 'bg-destructive/10';
    return 'bg-destructive/5';
  }
  return '';
}

function compactAmount(v: string): string {
  const n = Number(v);
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : n > 0 ? '+' : '';
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${sign}${(abs / 1_000).toFixed(1)}K`;
  if (abs === 0)             return '—';
  return `${sign}${abs.toFixed(0)}`;
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function MatrixCell({
  cell,
  isTotal = false,
}: {
  cell: GapMatrixCell;
  isTotal?: boolean;
}) {
  return (
    <td
      className={cn(
        'px-2 py-1.5 text-right text-xs tabular-nums',
        cellBg(cell.net),
        isTotal && 'font-semibold',
      )}
      title={`In: ${formatAmount(cell.inflow)} | Out: ${formatAmount(cell.outflow)}`}
    >
      <span className={netClass(cell.net)}>{compactAmount(cell.net)}</span>
    </td>
  );
}

function MatrixRow({
  row,
  bucketCount,
  isGrandTotal = false,
}: {
  row: GapMatrixRow;
  bucketCount: number;
  isGrandTotal?: boolean;
}) {
  return (
    <tr
      className={cn(
        'border-b',
        isGrandTotal
          ? 'bg-muted/60 font-bold'
          : 'hover:bg-muted/30',
      )}
    >
      <td
        className={cn(
          'sticky left-0 z-10 px-3 py-1.5 text-left text-xs font-medium',
          isGrandTotal
            ? 'bg-muted/60'
            : 'bg-background',
          'border-r',
        )}
      >
        {isGrandTotal ? (
          <span className="text-foreground">{row.label}</span>
        ) : (
          <span className="text-foreground">{row.label}</span>
        )}
      </td>
      {row.cells.map((cell, i) => (
        <MatrixCell key={i} cell={cell} isTotal={isGrandTotal} />
      ))}
      {/* Pad if fewer cells than expected (shouldn't happen, but defensive) */}
      {Array.from({ length: Math.max(0, bucketCount - row.cells.length) }).map((_, i) => (
        <td key={`pad-${i}`} className="px-2 py-1.5 text-right text-xs text-muted-foreground">—</td>
      ))}
      <MatrixCell cell={row.rowTotal} isTotal />
    </tr>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const DIM_LABELS: Record<MatrixDimension, string> = {
  INSTRUMENT_TYPE: 'Instrument type',
  BS_CLASS:        'BS class',
  CURRENCY:        'Currency',
  SENS:            'Sens',
  BS_POSITION:     'Bilan / Hors-bilan',
  PCEC:            'Chap. PCEC',
};

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Extract dim + segId + currency from a report config's parameters. */
function resolveConfigParams(cfg: ReportConfig) {
  const p = cfg.parameters as GapMatrixParams;
  const dim: MatrixDimension = (p.rowDimension as MatrixDimension) ?? 'INSTRUMENT_TYPE';
  const segId    = p.segmentationId ?? undefined;
  const currency = p.currency ?? undefined;
  return { dim, segId, currency };
}

// ── Main component ────────────────────────────────────────────────────────────

interface GapMatrixViewProps {
  /** ISO date pre-filled into the snapshot date picker. */
  defaultDate?: string;
  /**
   * When supplied by parent (e.g. GapReportView gap-type toggle),
   * configs whose name contains 'Liquidité'/'Liquidity' or 'Taux'/'Rate'
   * are prioritised in the auto-select. All configs still appear in the list.
   */
  gapType?: 'LIQUIDITY' | 'INTEREST_RATE';
}

export function GapMatrixView({ defaultDate, gapType }: GapMatrixViewProps) {
  const { data: allConfigs, isLoading: configsLoading } = useReportConfigs();
  const gapConfigs = allConfigs?.filter(c => c.reportType === 'GAP_MATRIX') ?? [];

  // Auto-select first config that matches the gapType hint (falls back to first overall)
  const preferredConfig = gapType
    ? (gapConfigs.find(c => {
        const n = c.name.toLowerCase();
        return gapType === 'LIQUIDITY'
          ? n.includes('liquid') || n.includes('liq')
          : n.includes('taux') || n.includes('rate') || n.includes('intérêt');
      }) ?? gapConfigs[0])
    : gapConfigs[0];
  const firstId = preferredConfig?.id ?? '';

  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [date, setDate] = useState(defaultDate ?? todayISO());

  // When configs load, select the preferred config
  useEffect(() => {
    if (!selectedConfigId && firstId) {
      setSelectedConfigId(firstId);
      const cfg = preferredConfig;
      if (cfg) {
        const p = cfg.parameters as GapMatrixParams;
        if (p.dateMode === 'SPECIFIC' && p.specificDate) setDate(p.specificDate);
        else setDate(defaultDate ?? todayISO());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstId]);

  // When user changes config, update date hint
  function handleConfigChange(id: string) {
    setSelectedConfigId(id);
    const cfg = gapConfigs.find(c => c.id === id);
    if (!cfg) return;
    const p = cfg.parameters as GapMatrixParams;
    if (p.dateMode === 'SPECIFIC' && p.specificDate) setDate(p.specificDate);
    else setDate(defaultDate ?? todayISO());
  }

  // Submitted = what was last run (drives the query)
  const [submitted, setSubmitted] = useState<{
    configId: string;
    date: string;
    dim: MatrixDimension;
    segId: string | undefined;
    currency: string | undefined;
  } | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const cfg = gapConfigs.find(c => c.id === selectedConfigId);
    if (!cfg) return;
    const { dim, segId, currency } = resolveConfigParams(cfg);
    setSubmitted({ configId: selectedConfigId, date, dim, segId, currency });
  }

  // Server-side full export (matrix + raw schedule detail) from cx-reporting.
  const [exporting, setExporting]   = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport() {
    if (!submitted) return;
    setExporting(true);
    setExportError(null);
    try {
      await downloadGapExport({
        date:           submitted.date,
        dim:            submitted.dim,
        segmentationId: submitted.segId,
        currency:       submitted.currency,
      });
    } catch (err) {
      setExportError((err as Error).message ?? 'Failed to download export.');
    } finally {
      setExporting(false);
    }
  }

  const { data, isFetching, isLoading, isError, error } = useGapMatrix(
    submitted?.date ?? todayISO(),
    submitted?.dim  ?? 'INSTRUMENT_TYPE',
    submitted?.currency,
    submitted?.segId,
    { enabled: !!submitted },
  );

  const buckets = data?.buckets ?? [];
  const rows    = data?.rows    ?? [];

  const activeConfig = submitted
    ? gapConfigs.find(c => c.id === submitted.configId)
    : null;

  function matrixTitle(): string {
    if (data?.segmentationName) return `Gap matrix — ${data.segmentationName}`;
    if (activeConfig) return `Gap matrix — ${activeConfig.name}`;
    return 'Gap matrix';
  }

  function matrixDimLabel(): string {
    if (data?.segmentationName) return data.segmentationName;
    if (submitted) return DIM_LABELS[submitted.dim] ?? submitted.dim;
    return 'Dimension';
  }

  const presets = gapConfigs.filter(c => c.isPreset);
  const custom  = gapConfigs.filter(c => !c.isPreset);

  return (
    <div className="space-y-4">
      {/* ── Report selector + run form ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Run gap matrix report
          </CardTitle>
          <CardDescription>
            Select a report configuration, choose a snapshot date and click Run.
            The dimension, segmentation and currency are defined in the report config.
            Manage report configs in <em>ALM Parameters → Report configurations</em>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>

            {/* Report selector */}
            <div className="space-y-1.5 flex-1 min-w-[220px]">
              <Label htmlFor="mx-config">Report</Label>
              {configsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground h-9">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading reports…
                </div>
              ) : gapConfigs.length === 0 ? (
                <p className="text-sm text-muted-foreground h-9 flex items-center">
                  No report configs found. Create one in ALM Parameters.
                </p>
              ) : (
                <select
                  id="mx-config"
                  value={selectedConfigId}
                  onChange={e => handleConfigChange(e.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {presets.length > 0 && (
                    <optgroup label="Standard reports">
                      {presets.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ★</option>
                      ))}
                    </optgroup>
                  )}
                  {custom.length > 0 && (
                    <optgroup label="Custom reports">
                      {custom.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}
            </div>

            {/* Date */}
            <div className="space-y-1.5">
              <Label htmlFor="mx-date">Snapshot date</Label>
              <Input
                id="mx-date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
                className="w-44"
              />
            </div>

            {/* Config summary badge */}
            {selectedConfigId && (() => {
              const cfg = gapConfigs.find(c => c.id === selectedConfigId);
              if (!cfg) return null;
              const p = cfg.parameters as GapMatrixParams;
              const parts = [
                DIM_LABELS[(p.rowDimension as MatrixDimension)] ?? p.rowDimension ?? 'Instrument type',
                p.segmentationId ? 'with segmentation' : null,
                p.currency ?? 'All currencies',
              ].filter(Boolean).join(' · ');
              return (
                <div className="flex items-center self-end mb-0.5">
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                    {parts}
                  </span>
                </div>
              );
            })()}

            <Button
              type="submit"
              disabled={!selectedConfigId || isFetching || configsLoading}
              className="self-end"
            >
              {isFetching
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…</>
                : <><Play className="mr-2 h-4 w-4" /> Run report</>
              }
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* ── Error banner ── */}
      {isError && error ? <ApiErrorBanner error={error} /> : null}

      {/* ── Matrix grid ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            {matrixTitle()}
            {submitted?.currency ? ` · ${submitted.currency}` : ' · All currencies (MAD)'}
          </CardTitle>
          <CardDescription>
            Net gap per {matrixDimLabel()} per bucket.
            Hover a cell to see inflow / outflow breakdown.
            Snapshot: <strong>{data?.asOfDate ?? submitted?.date ?? '—'}</strong>.
            {data?.segmentationName ? (
              <> Segmentation: <strong>{data.segmentationName}</strong>.</>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading matrix…
            </div>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No gold data for {data?.asOfDate ?? submitted?.date ?? '—'}.
              Run a gap calculation first or choose a different date.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {/* ── Header ── */}
                <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th
                      className="sticky left-0 z-20 border-r bg-muted/40 px-3 py-2 text-left font-medium"
                      style={{ minWidth: 140 }}
                    >
                      {matrixDimLabel()}
                    </th>
                    {buckets.map((b) => (
                      <th
                        key={b}
                        className="px-2 py-2 text-right font-medium"
                        style={{ minWidth: 80 }}
                      >
                        {b}
                      </th>
                    ))}
                    <th className="border-l px-2 py-2 text-right font-medium" style={{ minWidth: 80 }}>
                      Total
                    </th>
                  </tr>
                </thead>

                {/* ── Body ── */}
                <tbody>
                  {rows.map((row) => (
                    <MatrixRow
                      key={row.label}
                      row={row}
                      bucketCount={buckets.length}
                    />
                  ))}
                </tbody>

                {/* ── Grand total ── */}
                {data?.grandTotal ? (
                  <tfoot>
                    <MatrixRow
                      row={data.grandTotal}
                      bucketCount={buckets.length}
                      isGrandTotal
                    />
                  </tfoot>
                ) : null}
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Legend + Export ── */}
      {rows.length > 0 ? (
        <div className="flex flex-wrap items-center gap-4 px-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-5 rounded-sm bg-emerald-500/20" />
            Surplus (inflow &gt; outflow)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-5 rounded-sm bg-destructive/20" />
            Gap (outflow &gt; inflow)
          </span>
          <span className="italic">Net = inflow − outflow · amounts in MAD</span>

          {/* Full export — server-side ZIP (matrix + raw schedule detail) from cx-reporting */}
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !submitted}
            className="ml-auto flex items-center gap-1.5 rounded border border-input bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
            title="Download the full datamart export: aggregated matrix + underlying schedule detail (ZIP of two CSVs)"
          >
            {exporting
              ? <><Loader2 className="h-3 w-3 animate-spin" /> Exporting…</>
              : <><Download className="h-3 w-3" /> Export (full detail)</>}
          </button>
        </div>
      ) : null}

      {exportError ? (
        <div className="px-1">
          <ApiErrorBanner error={new Error(exportError)} />
        </div>
      ) : null}
    </div>
  );
}

// ── Shared error banner ────────────────────────────────────────────────────────

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code <code className="rounded bg-background/60 px-1 py-0.5">{error.code}</code>
            {error.field ? <> · field {error.field}</> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
